import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { Alert } from "@amzn/awsui-components-react/polaris";

import { RootState } from "../app/rootReducer";
import { VALIDATION_STATUS_NOTIFICATION_KEY } from "../notification/notificationConnector";

const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;
const VALIDATION_ROUTE_PREFIX = "/validation";

function isStale(runAtMs: number | undefined, nowMs: number): boolean {
  if (runAtMs === undefined) return false;
  return nowMs - runAtMs > TWENTY_FOUR_HOURS_MS;
}

function buildBody(lvsStale: boolean, hattrickStale: boolean): string {
  if (lvsStale && hattrickStale) {
    return "LVS and Hattrick validation data are older than 24 hours. Run each service to refresh.";
  }
  if (lvsStale) {
    return "LVS validation data is older than 24 hours. Run LVS to refresh.";
  }
  return "Hattrick validation data is older than 24 hours. Run Hattrick to refresh.";
}

/**
 * Standalone Cloudscape `Alert` that warns when the earliest known
 * per-service validation-run timestamp is older than 24 hours. The
 * body text names LVS, Hattrick, or both depending on which service
 * is stale, so a user who just ran one service can tell whether the
 * banner is complaining about the other. Dismissal is per-pair of
 * timestamps: a new run of either service resets the dismiss state
 * so the next stale window is announced again.
 */
export const StaleDataAlert: React.FC = () => {
  // Rendered above <Routes> in the App Shell, so it mounts on every
  // path. Gate on the current route so the validation-specific alert
  // doesn't surface on /search, /baseline, /launch, etc. after the
  // user has visited the dashboard.
  const location = useLocation();
  const onValidationRoute = location.pathname.startsWith(
    VALIDATION_ROUTE_PREFIX
  );

  const earliestLvsRunAtMs = useSelector(
    (state: RootState) => state.statusOverviewAggregation.earliestLvsRunAtMs
  );
  const earliestHattrickRunAtMs = useSelector(
    (state: RootState) =>
      state.statusOverviewAggregation.earliestHattrickRunAtMs
  );

  const hasActiveValidationStatus = useSelector(
    (state: RootState) =>
      state.notification.notificationItems[
        VALIDATION_STATUS_NOTIFICATION_KEY
      ] !== undefined
  );

  const now = Date.now();
  const lvsStale = isStale(earliestLvsRunAtMs, now);
  const hattrickStale = isStale(earliestHattrickRunAtMs, now);
  const anyStale = lvsStale || hattrickStale;

  // Key dismissal on both the timestamps and the current stale set.
  // Timestamps changing (a service re-ran) resets the dismiss; the
  // stale-set component also resets the dismiss when a second service
  // crosses the 24h threshold with its timestamp unchanged, so a user
  // who dismissed the LVS-only banner still sees the "LVS and
  // Hattrick" one once Hattrick goes stale too.
  const dismissKey = useMemo(
    () =>
      `${earliestLvsRunAtMs ?? ""}|${earliestHattrickRunAtMs ?? ""}|${
        lvsStale ? "L" : ""
      }|${hattrickStale ? "H" : ""}`,
    [earliestLvsRunAtMs, earliestHattrickRunAtMs, lvsStale, hattrickStale]
  );

  const [dismissedForKey, setDismissedForKey] = useState<string | undefined>(
    undefined
  );

  useEffect(() => {
    if (dismissedForKey !== undefined && dismissedForKey !== dismissKey) {
      setDismissedForKey(undefined);
    }
  }, [dismissKey, dismissedForKey]);

  const dismissed = dismissedForKey === dismissKey;

  if (
    !onValidationRoute ||
    !anyStale ||
    dismissed ||
    hasActiveValidationStatus
  ) {
    return null;
  }

  return (
    <Alert
      data-testid="validation-dashboard-stale-data-alert"
      type="warning"
      header="Stale Validation Data"
      dismissible
      i18nStrings={{
        warningIconAriaLabel: "Warning",
        dismissAriaLabel: "Dismiss stale validation data alert",
      }}
      onDismiss={() => setDismissedForKey(dismissKey)}
    >
      {buildBody(lvsStale, hattrickStale)}
    </Alert>
  );
};

export default StaleDataAlert;
